import React from 'react'
import { Pause } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { RewardCatalogIcon } from '@/src/renderer/components/ui/RewardCatalogIcon'
import { getRewardById, rewardName, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import { formatFocusClock } from '@/src/renderer/lib/focus'
import type { FocusCardProps } from './FocusCard.types'
import './FocusCard.scss'

/**
 * **Das Fokus-Fenster** — alles, was von HappyTask zu sehen ist, solange ein
 * **Fokus** läuft. Auf dem Desktop ein eigenes Fenster oben rechts (3b), im Web
 * die Mitte einer Fläche, die die App zudeckt (3d).
 *
 * **Es steht auf Glas** (Figma 5420-53414): Das Fenster nimmt auf, was hinter
 * ihm liegt, und die Karte legt nur eine Tönung darüber. Die bleibt **dunkel**,
 * auf hellem wie dunklem Schreibtisch — es ist ein eigenes Ding dort draußen,
 * kein Fenster der App. Der **Lebensbereich** färbt seit dem neuen Schnitt nur
 * noch Haken und Fortschritt, nicht mehr den Grund.
 *
 * **Zwei Zeilen:** oben das **Projekt**, darunter der Aufgabentitel über
 * höchstens zwei Zeilen.
 *
 * **Drei Zustände.** In Ruhe steht rechts die Zeit, darunter die Belohnung.
 * Beim Überfahren tritt der Pause-Knopf an ihre Stelle; unter dem Zeiger nimmt
 * er die Farbe des **Lebensbereichs** an. Die Fünf-Minuten-Regel von früher („in Ruhe tickt nichts") ist
 * damit hinfällig — der Knopf ersetzt die Zeit, sie wäre sonst nie wieder zu
 * sehen (entschieden 22.09.2026).
 *
 * Reine Props: keine Datenquelle, kein IPC. Woher Zeit und Aufgabe kommen, weiß
 * die Hülle (`useFocusClock` + `useDataSource`).
 *
 * @example
 * <FocusCard
 *   projectName="Urlaubsliste"
 *   title="Koffer packen & Badsachen"
 *   lifeSpace="personal"
 *   seconds={264}
 *   targetSeconds={1500}
 *   rewardId="swimming"
 *   subtasks={{ done: 3, total: 4 }}
 *   onPause={endFocus}
 *   onComplete={completeTask}
 * />
 */
export const FocusCard: React.FC<FocusCardProps> = ({
  title,
  projectName,
  lifeSpace,
  seconds,
  targetSeconds,
  rewardId,
  subtasks,
  onPause,
  onComplete,
  Heading = 'p',
  className,
}) => {
  const { t, i18n } = useTranslation()

  const remaining = targetSeconds == null ? null : targetSeconds - seconds
  const overtime = remaining !== null && remaining < 0
  // Mit Dauer läuft die Uhr herunter, ohne Dauer hoch. In der Überzeit trägt
  // `formatFocusClock` das Pluszeichen. Ab 5 Minuten zeigt sie nur noch
  // Minuten („6m").
  const clock = formatFocusClock(remaining ?? seconds)

  const reward = rewardId ? getRewardById(rewardId) : undefined
  const rewardLabel = reward ? rewardName(reward, (i18n.language?.slice(0, 2) ?? 'de') as CatalogLanguage) : ''

  const hasSubtasks = !!subtasks && subtasks.total > 0
  const done = hasSubtasks ? Math.min(subtasks.done, subtasks.total) : 0
  const progress = hasSubtasks ? (done / subtasks.total) * 100 : 0

  const classes = [
    'focus-card',
    overtime ? 'focus-card--overtime' : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <div className={classes} data-space={lifeSpace}>
      <button
        type="button"
        className="focus-card__done"
        onClick={onComplete}
        aria-label={t('focusCard.complete')}
      >
        <span className="focus-card__done-circle" />
      </button>

      <div className="focus-card__content">
        {hasSubtasks && (
          <div
            className="focus-card__progress"
            role="img"
            aria-label={t('focusCard.subtaskProgress', { done, total: subtasks.total })}
          >
            <span className="focus-card__progress-track" />
            <span className="focus-card__progress-value" style={{ width: `${progress}%` }} />
          </div>
        )}

        {/*
          Die Überschrift ist das PROJEKT, nicht der Titel: Radix verlangt im
          Dialog des Web-Überzugs genau ein `Dialog.Title`, und die erste Zeile
          ist die, die ein Vorleser zuerst bringen soll. Ohne Projekt fällt sie
          weg — dann trägt der Titel sie (siehe unten).
        */}
        <div className="focus-card__lines">
          {projectName
            ? <Heading className="focus-card__project">{projectName}</Heading>
            : null}
          {projectName
            ? <p className="focus-card__title">{title}</p>
            : <Heading className="focus-card__title">{title}</Heading>}
        </div>
      </div>

      {/*
        Rechts eine Spalte: Zeit oben, Belohnung darunter. Beim Überfahren tritt
        der Pause-Knopf an ihre Stelle — er liegt darüber, damit die Breite
        gleich bleibt und der Titel nicht neu umbricht.
      */}
      <div className="focus-card__side" aria-hidden={false}>
        <span
          className="focus-card__time"
          aria-label={remaining === null
            ? t('focusCard.elapsed', { time: clock })
            : t('focusCard.remaining', { time: clock })}
        >
          {clock}
        </span>

        {reward && (
          <RewardCatalogIcon
            rewardId={rewardId!}
            name={rewardLabel}
            size={32}
            className="focus-card__reward"
          />
        )}
      </div>

      <button
        type="button"
        className="focus-card__pause"
        onClick={onPause}
        aria-label={t('focusCard.pause')}
      >
        <Pause size={16} strokeWidth={0} fill="currentColor" />
      </button>
    </div>
  )
}
